/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { defineSlotRecipe } from 'chakra-v3';

/**
 * Radio Group Slot Recipe
 *
 * Custom styling for RadioGroup component with outline variant.
 * This recipe customizes the appearance of radio buttons to match the design system.
 *
 * Usage:
 * ```tsx
 * <RadioGroup.Root variant="outline">
 *   <RadioGroup.Item value="option1">
 *     <RadioGroup.ItemHiddenInput />
 *     <RadioGroup.ItemIndicator />
 *     <RadioGroup.ItemText>Option 1</RadioGroup.ItemText>
 *   </RadioGroup.Item>
 * </RadioGroup.Root>
 * ```
 */
export const radioGroupRecipe = defineSlotRecipe({
  slots: ['root', 'item', 'itemIndicator', 'itemText'],
  base: {
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    item: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer',
    },
    itemIndicator: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'brand.white',
      backgroundColor: 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      _checked: {
        borderColor: 'brand.secondaryBlue2',
        backgroundColor: 'brand.cta',
      },
    },
    itemText: {
      fontFamily: 'body',
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '1.4',
      letterSpacing: '0.08px',
      color: 'text.primary',
      _checked: {
        color: 'brand.secondaryBlue2',
      },
    },
  },
  variants: {
    variant: {
      outline: {
        itemIndicator: {
          borderColor: 'brand.white',
          backgroundColor: 'transparent',
          _checked: {
            borderColor: 'brand.secondaryBlue2',
            backgroundColor: 'brand.cta',
          },
        },
        itemText: {
          color: 'brand.white',
          _checked: {
            color: 'brand.secondaryBlue2',
          },
        },
      },
      solid: {
        itemIndicator: {
          borderColor: 'brand.teal',
          backgroundColor: 'transparent',
          _checked: {
            borderColor: 'brand.teal',
            backgroundColor: 'brand.teal',
          },
        },
      },
      subtle: {
        itemIndicator: {
          borderColor: 'text.primary',
          backgroundColor: 'transparent',
          _checked: {
            borderColor: 'brand.teal',
            backgroundColor: 'color-mix(in srgb, #0A313C 10%, transparent)',
          },
        },
      },
    },
  },
  defaultVariants: {
    variant: 'outline',
  },
});
